// TODO don't use `dangerouslySetInnerHTML`
/* eslint-disable react/no-danger */
import { Responsibility, Qualification, RoleResponse } from '@careerhub/api';
import {
  CareerHubModuleReactContext,
  OpenCopilotButton,
  TBDDescriptionMessage,
  addBookmark,
  careerStateSelector,
  loadBookmarks,
  removeBookmark,
  useSharedStyles,
} from '@careerhub/common';
import {
  Body1,
  Button,
  Divider,
  Image,
  Subtitle2,
  Tab,
  TabList,
  Title3,
  Text,
  Dropdown,
  Option,
  Body1Strong,
  SelectTabData,
} from '@fluentui/react-components';
import {
  ArrowLeft16Regular,
  Bookmark16Filled,
  Bookmark16Regular,
  Checkmark16Regular,
  CircleSmall20Filled,
  Copy16Regular,
  Filter12Regular,
} from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { EventName, FeatureName, SubFeatureName } from '../../components/resources/TelemetryEventDetails';
import { useLevelThreeStyles } from './LevelThreePage.styles';
import { useDispatch, useSelector } from 'react-redux';
import { Announced } from 'office-ui-fabric-react';
import { SkillsMenu } from './SkillsMenu';
import { FeatureFlights } from '../../utilities/FeatureFlights';
import { filterConstants } from '../../Constant';

interface RouteParams {
  levelThreeId: string;
}

const SHARE_CONFIRMATION_DURATION_MS = 10000;

/** Full page view of a role: requirements, skills, etc. */
export const LevelThreePage: React.FC = () => {
  const styles = useLevelThreeStyles();
  const sharedStyles = useSharedStyles();
  const history = useHistory();
  const backLinkRef = React.useRef<HTMLAnchorElement>(null);
  const bookmarkButtonRef = React.useRef<HTMLButtonElement>(null);
  const bookmarkActionPendingRef = React.useRef(false);
  const copyAnnouncementTimerRef = React.useRef<number | null>(null);
  const shareConfirmationTimerRef = React.useRef<number | null>(null);
  const { levelThreeId } = useParams<RouteParams>();
  const hash = useLocation().hash;
  const [roleData, setRoleData] = React.useState<RoleResponse>();
  const [professioName, setProfessioName] = React.useState<string>();
  const [disciplineName, setDisciplineName] = React.useState<string>();
  const [copyConfirmed, setCopyConfirmed] = React.useState(false);
  const [bookmarked, setBookmarked] = React.useState(false);
  const [announceText, setAnnounceText] = React.useState('');
  const [shareAnnouncement, setShareAnnouncement] = React.useState('');
  const [selectedFilterOption, setSelectedFilterOption] = React.useState<string>(filterConstants.all);
  const [isExpandAll, setisExpandAll] = React.useState<boolean>(false);
  const [seeMoreCriticalSkills, setSeeMoreCriticalSkills] = React.useState<boolean>(false);
  const { bookmarks } = useSelector(careerStateSelector);
  const dispatch = useDispatch();
  const { telemetryClient, roleLibraryService } = React.useContext(CareerHubModuleReactContext);

  const filterOption = [filterConstants.all, filterConstants.required, filterConstants.notRequired];

  React.useEffect(() => {
    roleLibraryService
      .getRoleDetails(levelThreeId)
      .then((fetchedRoleData) => {
        roleLibraryService.getTaxonomicRank(fetchedRoleData.parentTaxonomies[0].taxonomyTypeId).then((parentData) => {
          let filterData = parentData.filter((item) => item.id == fetchedRoleData.parentTaxonomies[0].id);

          setProfessioName(filterData[0].parentTaxonomies[0].name);
          setDisciplineName(filterData[0].name);
        });
        if (fetchedRoleData.description === TBDDescriptionMessage) {
          telemetryClient.logEvent(FeatureName.RolePage, {
            EventName: EventName.UserSawTBD,
            roleId: fetchedRoleData.id,
          });
        }
        setRoleData(fetchedRoleData);
        setAnnounceText(`${fetchedRoleData?.name} page`);
      })
      .catch((error) => {
        console.error('Error fetching role details:', error);
        history.push('/');
      });
  }, [history, levelThreeId, roleLibraryService, telemetryClient]);

  React.useEffect(() => {
    if (backLinkRef.current) {
      backLinkRef.current.focus();
    }
  }, []);

  React.useEffect(
    () => () => {
      if (shareConfirmationTimerRef.current !== null) {
        window.clearTimeout(shareConfirmationTimerRef.current);
      }
      if (copyAnnouncementTimerRef.current !== null) {
        window.clearTimeout(copyAnnouncementTimerRef.current);
      }
    },
    []
  );

  const NotAvailable: React.FC = () => <Body1 className={styles.text}>Not available</Body1>;

  const [selectedMenuItem, setSelectedMenuItem] = useState(hash.substring(1) || 'roleSummary');

  const onTabSelect = (_event: unknown, data: SelectTabData) => {
    if (typeof data.value !== 'string') return;

    telemetryClient.logButtonClick(FeatureName.RolePage, SubFeatureName.TabList, { tabName: data.value });
    setSelectedMenuItem(data.value);
  };

  const onBackClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    telemetryClient.logButtonClick(FeatureName.RolePage, SubFeatureName.BackClick, {});
    history.goBack();
  };

  const onCopyClick = async () => {
    if (copyConfirmed) return;

    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopyConfirmed(true);
      setShareAnnouncement('');
      if (copyAnnouncementTimerRef.current !== null) {
        window.clearTimeout(copyAnnouncementTimerRef.current);
      }
      copyAnnouncementTimerRef.current = window.setTimeout(() => {
        setShareAnnouncement('Copied to clipboard');
        copyAnnouncementTimerRef.current = null;
      }, 50);
    } catch (e) {
      throw e;
    }
    shareConfirmationTimerRef.current = window.setTimeout(() => {
      setCopyConfirmed(false);
      shareConfirmationTimerRef.current = null;
    }, SHARE_CONFIRMATION_DURATION_MS);
  };

  const onBookmarkClick = React.useCallback(() => {
    if (roleData) {
      bookmarkActionPendingRef.current = true;
      dispatch(bookmarked ? removeBookmark(roleData.id!) : addBookmark(roleData.id!));
    }
    telemetryClient.logPinUnpinChange(FeatureName.RolePage, SubFeatureName.Bookmark, bookmarked, {
      roleId: roleData?.id,
    });
  }, [dispatch, bookmarked, roleData, telemetryClient]);

  React.useEffect(() => {
    dispatch(loadBookmarks());
  }, [dispatch]);

  const prevBookmarkedRef = React.useRef<boolean | null>(null);

  React.useEffect(() => {
    if (!announceText) return;

    const announcementTimer = window.setTimeout(() => setAnnounceText(''), 1000);
    return () => window.clearTimeout(announcementTimer);
  }, [announceText]);

  React.useEffect(() => {
    if (roleData) {
      const isBookMarked = bookmarks.some((bookmark) => bookmark.id === roleData.id);
      setBookmarked(isBookMarked);
      // Only announce bookmark changes, not initial load
      if (prevBookmarkedRef.current !== null && prevBookmarkedRef.current !== isBookMarked) {
        setAnnounceText(isBookMarked ? `Bookmarked for ${roleData.name}` : `Bookmark removed for ${roleData.name}`);
        if (bookmarkActionPendingRef.current) {
          bookmarkButtonRef.current?.focus();
          bookmarkActionPendingRef.current = false;
        }
      }
      prevBookmarkedRef.current = isBookMarked;
    }
  }, [bookmarks, roleData]);

  const { featureService } = React.useContext(CareerHubModuleReactContext);

  const skillsAndCapabilities = featureService.isFeatureEnabled(FeatureFlights.SkillsAndCapabilities);

  const renderQualification = (data: Qualification[] | []) => {
    const minimum = data.filter((item) => item.tags.includes('Minimum'));
    const preferred = data.filter((item) => item.tags.includes('Preferred'));
    return (
      <>
        <Subtitle2 as="h3" className={styles.text}>
          Required/minimum qualifications
        </Subtitle2>
        {minimum && minimum.length > 0 ? (
          minimum.map((minimumQualifications: Qualification) => {
            return (
              <Body1 className={styles.section}>
                <span dangerouslySetInnerHTML={{ __html: minimumQualifications.name || '' }} />
              </Body1>
            );
          })
        ) : (
          <NotAvailable />
        )}
        {preferred && preferred.length > 0 && (
          <div>
            <Subtitle2 as="h3" className={styles.text}>
              Additional or preferred qualifications
            </Subtitle2>
            {preferred.map((prefer: Qualification) => {
              return (
                <Body1 className={styles.section}>
                  <span dangerouslySetInnerHTML={{ __html: prefer.name || '' }} />
                </Body1>
              );
            })}
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.operationsContainer}>
        <Button
          as="a"
          href="#"
          ref={backLinkRef}
          appearance="transparent"
          className={styles.buttonLabel}
          icon={<ArrowLeft16Regular aria-hidden="true" />}
          onClick={onBackClick}
          aria-label="Back to previous page"
        >
          Back
        </Button>
        <Divider aria-hidden="true" vertical style={{ maxWidth: '2px' }} />
        <Button
          aria-disabled={copyConfirmed}
          icon={copyConfirmed ? <Checkmark16Regular aria-hidden="true" /> : <Copy16Regular aria-hidden="true" />}
          className={styles.buttonColor}
          onClick={onCopyClick}
          appearance="transparent"
        >
          {copyConfirmed ? 'Copied to clipboard' : 'Copy'}
        </Button>
        <div role="status" aria-live="polite" aria-atomic="true" className={styles.srOnly}>
          {shareAnnouncement}
        </div>
        <Button
          ref={bookmarkButtonRef}
          icon={bookmarked ? <Bookmark16Filled aria-hidden="true" /> : <Bookmark16Regular aria-hidden="true" />}
          aria-label={`${bookmarked ? 'Filled' : 'Unfilled'} Bookmark for ${roleData?.name ?? 'role'}`}
          aria-pressed={bookmarked}
          onClick={onBookmarkClick}
          className={styles.buttonColor}
          appearance="transparent"
        >
          {bookmarked ? 'Bookmarked' : 'Bookmark'}
        </Button>
        <Announced message={announceText} aria-live="assertive" className={styles.srOnly} />
      </div>
      {window.innerWidth <= 640 && <OpenCopilotButton />}
      <div className={sharedStyles.flexSpaceBetween}>
        <Title3 className={styles.title} as="h2">
          {roleData?.name}
        </Title3>
        {window.innerWidth >= 640 && <OpenCopilotButton />}
      </div>
      <Subtitle2 className={styles.subtitle}>
        Profession: <Text className={styles.subtitleColor}>{professioName}</Text>
      </Subtitle2>
      <Subtitle2 className={styles.subtitle}>
        Discipline: <Text className={styles.subtitleColor}>{disciplineName}</Text>
      </Subtitle2>
      <div className={styles.tabList}>
        <TabList
          className={styles.roleTabList}
          selectedValue={selectedMenuItem}
          reserveSelectedTabSpace={false}
          onTabSelect={onTabSelect}
        >
          <Tab className={styles.tab} value={'roleSummary'}>
            <span className={styles.unSelectedTabName}>Role summary</span>
          </Tab>
          <Tab className={styles.tab} value={'skills'}>
            <span className={styles.unSelectedTabName}>
              {skillsAndCapabilities ? 'Capabilities & Skills' : 'Skills'}
            </span>
          </Tab>
          <Tab className={styles.tab} value={'responsibilities'}>
            <span className={styles.unSelectedTabName}>Responsibilities</span>
          </Tab>
          <Tab className={styles.tab} value={'qualifications'}>
            <span className={styles.unSelectedTabName}>Qualifications</span>
          </Tab>
        </TabList>
        {selectedMenuItem === 'skills' &&
          skillsAndCapabilities &&
          roleData?.categorizedSkills &&
          roleData?.categorizedSkills.length > 0 && (
            <div className={styles.dropdownContainerStyle}>
              <Dropdown
                className={styles.dropdownStyle}
                defaultValue={filterOption[0]}
                selectedOptions={[selectedFilterOption]}
                button={
                  <span className={styles.truncatedText}>
                    <Filter12Regular aria-hidden="true" style={{ marginRight: '8px' }} />
                    {selectedFilterOption}
                  </span>
                }
                aria-label="Skills Filter"
                onOptionSelect={(_e, data) => setSelectedFilterOption(data.optionText ?? filterOption[0])}
                size="medium"
              >
                {filterOption.map((option) => {
                  return (
                    <Option value={option} key={option}>
                      {option}
                    </Option>
                  );
                })}
              </Dropdown>
              <div className={styles.expandAll}>
                <Button
                  appearance="secondary"
                  size="medium"
                  onClick={() => {
                    setisExpandAll(!isExpandAll);
                  }}
                  aria-label={isExpandAll ? 'Collapse all capabilities' : 'Expand all capabilities'}
                >
                  {isExpandAll ? 'Collapse all capabilities' : 'Expand all capabilities'}
                </Button>
              </div>
            </div>
          )}
      </div>
      {selectedMenuItem === 'roleSummary' && (
        <>
          <Body1 as="p">{roleData?.description}</Body1>
        </>
      )}
      {selectedMenuItem === 'skills' && (
        <>
          {skillsAndCapabilities ? (
            <div>
              {roleData && roleData.categorizedSkills?.length > 0 ? (
                <SkillsMenu
                  filter={selectedFilterOption}
                  isExpandAll={isExpandAll}
                  setShowCollapseAll={(isExpand) => setisExpandAll(isExpand)}
                  categorizedSkills={roleData?.categorizedSkills}
                />
              ) : (
                <NotAvailable />
              )}
            </div>
          ) : (
            <div>
              <div className={styles.skillsSubtitleContainer}>
                <Image src="../../../assets/skillsBullet.svg" alt="" aria-hidden="true" />
                <Subtitle2 as="h3" className={styles.skillsSubtitle}>
                  Required Upon Entry
                </Subtitle2>
              </div>
              {roleData?.criticalSkills && roleData.criticalSkills.length > 0 ? (
                <div className={styles.section}>
                  <div className={styles.textContentsContainer}>
                    {roleData.criticalSkills
                      .slice(0, seeMoreCriticalSkills ? roleData.criticalSkills.length : 8)
                      .map((skill) => (
                        <div className={styles.textContents}>
                          <Body1Strong as="h4">{skill.name}</Body1Strong>
                          <Body1>{skill.description}</Body1>
                        </div>
                      ))}
                  </div>
                  {roleData.criticalSkills.length > 8 && (
                    <Button
                      className={styles.seeMoreButton}
                      appearance="transparent"
                      size="large"
                      onClick={() => {
                        telemetryClient.logButtonClick(FeatureName.RolePage, SubFeatureName.SeeMoreAndLess, {
                          categoryName: 'Required Upon Entry',
                          clickedType: seeMoreCriticalSkills ? 'See less' : 'See more',
                        });
                        setSeeMoreCriticalSkills(!seeMoreCriticalSkills);
                      }}
                    >
                      {seeMoreCriticalSkills ? 'See less' : 'See more'}
                    </Button>
                  )}
                </div>
              ) : (
                <NotAvailable />
              )}
            </div>
          )}
        </>
      )}

      {selectedMenuItem === 'responsibilities' && (
        <>
          {roleData?.categorizedResponsibilities && roleData.categorizedResponsibilities.length > 0 ? (
            <>
              {roleData.categorizedResponsibilities.map((categorizedResponsibility) => {
                const { responsibilities, category } = categorizedResponsibility;
                return (
                  <div>
                    <div className={styles.responsibilityTitleContainer}>
                      <Image
                        src="../../../assets/Rectangle.svg"
                        className={styles.bulletImg}
                        alt=""
                        aria-hidden="true"
                      />
                      <Text as="h3" className={styles.responsibilitiesSubtitle}>
                        {category.name}
                      </Text>
                    </div>
                    {responsibilities.map((responsibility: Responsibility) => {
                      return (
                        <div className={styles.responsibilitiesContainer}>
                          <CircleSmall20Filled className={styles.buttonIcon} />
                          <div>
                            <Body1>
                              <span dangerouslySetInnerHTML={{ __html: responsibility.description || '' }} />{' '}
                            </Body1>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </>
          ) : (
            <NotAvailable />
          )}
        </>
      )}
      {selectedMenuItem === 'qualifications' && <>{renderQualification(roleData?.qualifications || [])}</>}
    </div>
  );
};
